import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { filter, map, Observable, switchMap } from 'rxjs';
import { environment } from '../../environments/environment';
import { Job } from '../class/job.class';
import { JobModel } from '../models/job.model';
import { WsUpdateTaskMessage } from '../models/ws-update-task-message.model';
import { updateJobsFromWsMessage } from '../helpers/job.helper';
import { DownloadService } from './download.service';
import { JobFileEnum } from '../enums/job-file.eum';
import { TranscriptionSegment } from '../models/translated-segment.model';

@Injectable({
  providedIn: 'root',
})
export class JobService {
  downloadService: DownloadService = inject(DownloadService);

  getAudioFinal(jobId: number): Observable<Blob> {
    return this.downloadService
      .downloadAsset(jobId, JobFileEnum.FINALE_AUDIO)
      .pipe(
        filter((event: any) => event.type === 4 && !!event.body),
        map((event: any) => event.body as Blob),
      );
  }

  getOriginalAudio(jobId: number): Observable<Blob> {
    return this.downloadService
      .downloadAsset(jobId, JobFileEnum.ORIGINAL_AUDIO)
      .pipe(
        filter((event: any) => event.type === 4 && !!event.body),
        map((event: any) => event.body as Blob),
      );
  }

  getOriginalVideo(jobId: number): Observable<Blob> {
    return this.downloadService
      .downloadAsset(jobId, JobFileEnum.ORIGINAL_VIDEO_CLEAN)
      .pipe(
        filter((event: any) => event.type === 4 && !!event.body),
        map((event: any) => event.body as Blob),
      );
  }

  getVideoFinal(jobId: number): Observable<Blob> {
    return this.downloadService
      .downloadAsset(jobId, JobFileEnum.FINAL_VIDEO)
      .pipe(
        filter((event: any) => event.type === 4 && !!event.body),
        map((event: any) => event.body as Blob),
      );
  }

  getTranscription(jobId: number): Observable<TranscriptionSegment[]> {
    return this.downloadService
      .downloadAsset(jobId, JobFileEnum.TRANSCRIPTION_TXT)
      .pipe(
        filter((event: any) => event.type === 4 && !!event.body),
        map((event: any) => event.body as Blob),
        switchMap((blob: Blob) => blob.text()),
        map((text: string) => JSON.parse(text)),
      );
  }

  constructor(private http: HttpClient) {}

  getClientJobs(): Observable<Job[]> {
    const url: string = `${environment.apiUrl}/jobs`;
    return this.http.get<JobModel[]>(url).pipe(
      map((jobs: JobModel[]) => {
        return jobs.map((job: JobModel) => {
          return new Job(job);
        });
      }),
    );
  }

  getClientJobById(id: string): Observable<Job> {
    const url: string = `${environment.apiUrl}/jobs/${id}`;
    return this.http
      .get<JobModel>(url)
      .pipe(map((job: JobModel) => new Job(job)));
  }

  delete(id: number): Observable<any> {
    const url: string = `${environment.apiUrl}/jobs/${id}`;
    return this.http.delete(url);
  }

  /**
   * Met à jour la tâche d'un job à partir d'un message WebSocket
   * @param jobs Liste des jobs actuels
   * @param message Message reçu via WebSocket
   * @returns Nouvelle liste de jobs avec la tâche mise à jour
   */
  updateJobsFromWsMessage(jobs: Job[], message: WsUpdateTaskMessage): Job[] {
    return updateJobsFromWsMessage(jobs, message);
  }

  postSoundsParameters(
    jobId: number,
    taskId: number,
    originalVoiceVolume: number,
    translatedVoiceVolume: number,
    musicsAndSoundsVolume: number,
  ): Observable<any> {
    const url: string = `${environment.apiUrl}/jobs/${jobId}/${taskId}/sound-parameters`;
    return this.http.post(url, {
      originalVoiceVolume: originalVoiceVolume,
      musicAfxVolume: musicsAndSoundsVolume,
      translatedVoiceVolume: translatedVoiceVolume,
    });
  }
}
